import { createAbortError, ProtocolError, SandboxError } from './errors';
import { cacheKeySource } from './hash';
import { LruCache } from './lru';
import { checkCode, checkRunRequest, validateFmtResponse, validateRunResponse } from './protocol';
import type {
  FmtResponse,
  RunOptions,
  RunRequest,
  RunResponse,
  SandboxClient,
  SandboxClientDeps,
  SandboxPath,
} from './types';

export const CLIENT_TIMEOUT_MS = 180_000;
export const CACHE_SIZE = 32;
/** 自動再試行の基準待ち時間（§4.4）。1 回目 2,000ms、2 回目 4,000ms。 */
export const RETRY_BASE_MS = [2_000, 4_000] as const;
export const RETRY_MAX_WAIT_MS = 30_000;

/**
 * 再試行の待ち時間（sandboxd-client.md §4.4）。
 * 基準 × [0.8, 1.2) と Retry-After（秒）の大きい方。上限 30,000ms。
 */
export function retryWaitMs(attempt: 1 | 2, random: number, retryAfter: string | null): number {
  const base = (attempt === 1 ? RETRY_BASE_MS[0] : RETRY_BASE_MS[1]) * (0.8 + 0.4 * random);
  let wait = base;
  if (retryAfter !== null && /^\s*\d+(\.\d+)?\s*$/.test(retryAfter)) {
    wait = Math.max(wait, Number(retryAfter) * 1000);
  }
  return Math.round(Math.min(wait, RETRY_MAX_WAIT_MS));
}

/** HTTP ステータスから分類する（§2.3, §4.6）。200 と 429 / 503 は呼び出し側で扱う。 */
export function classifyStatus(status: number): 'rejected' | 'timeout' | 'server' | 'busy' {
  if (status === 400 || status === 403 || status === 405 || status === 413) return 'rejected';
  if (status === 504) return 'timeout';
  if (status === 429 || status === 503) return 'busy';
  return 'server';
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function serverMessageOf(body: unknown): string | null {
  if (typeof body === 'object' && body !== null && 'error' in body) {
    const e = body.error;
    if (typeof e === 'string') return e;
  }
  return null;
}

/**
 * sandboxd クライアントを作る（sandboxd-client.md §4）。
 * アプリ全体で 1 つだけ作り、同時実行 1 を守る（§4.2）。
 */
export function createSandboxClient(deps: SandboxClientDeps): SandboxClient {
  const baseUrl = deps.baseUrl ?? '';
  const timeoutMs = deps.timeoutMs ?? CLIENT_TIMEOUT_MS;
  const timeoutSignal = deps.timeoutSignal ?? ((ms: number) => AbortSignal.timeout(ms));
  const cache = new LruCache<unknown>(deps.cacheSize ?? CACHE_SIZE);
  let busy = false;

  async function request<T>(
    path: SandboxPath,
    body: string,
    keySource: string,
    validate: (b: unknown) => T,
    opts: RunOptions,
  ): Promise<T> {
    const { signal } = opts;
    if (signal?.aborted) throw createAbortError();
    const key = await deps.sha256Hex(keySource);
    if (opts.cache !== 'bypass') {
      const hit = cache.get(key);
      if (hit !== undefined) return validate(structuredClone(hit));
    }

    for (let attempt = 0; ; attempt++) {
      if (signal?.aborted) throw createAbortError();
      const timeout = timeoutSignal(timeoutMs);
      const combined = signal ? AbortSignal.any([timeout, signal]) : timeout;
      let res: Response;
      let text: string;
      try {
        res = await deps.fetch(baseUrl + path, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(deps.headers?.({ path, attempt }) ?? {}),
          },
          body,
          signal: combined,
        });
        deps.onResponse?.({ path, status: res.status, headers: res.headers, attempt });
        text = await res.text();
      } catch (e) {
        if (signal?.aborted) throw createAbortError();
        if (timeout.aborted) throw new SandboxError('timeout');
        if (e instanceof SandboxError) throw e;
        throw new SandboxError('network');
      }

      if (res.status === 200) {
        const parsed = parseJson(text);
        let value: T;
        try {
          value = validate(parsed);
        } catch (e) {
          if (e instanceof ProtocolError) throw new SandboxError('protocol', 200);
          throw e;
        }
        cache.set(key, structuredClone(value));
        return value;
      }

      const serverMessage = serverMessageOf(parseJson(text));
      const kind = classifyStatus(res.status);
      if (kind !== 'busy') throw new SandboxError(kind, res.status, serverMessage);
      if (attempt >= 2) throw new SandboxError('busy', res.status, serverMessage);
      const next: 1 | 2 = attempt === 0 ? 1 : 2;
      const waitMs = retryWaitMs(next, deps.random(), res.headers.get('Retry-After'));
      opts.onRetry?.(next, waitMs);
      try {
        await deps.sleep(waitMs, signal);
      } catch {
        throw createAbortError();
      }
      if (signal?.aborted) throw createAbortError();
    }
  }

  async function exclusive<T>(fn: () => Promise<T>): Promise<T> {
    if (busy) throw new SandboxError('local-busy');
    busy = true;
    try {
      return await fn();
    } finally {
      busy = false;
    }
  }

  return {
    get busy() {
      return busy;
    },
    run(req: RunRequest, opts: RunOptions = {}): Promise<RunResponse> {
      return exclusive(async () => {
        checkRunRequest(req);
        const path = '/api/run';
        return request(
          path,
          JSON.stringify({ code: req.code, stdins: req.stdins }),
          cacheKeySource(path, req.code, req.stdins),
          (b) => validateRunResponse(b, req.stdins.length),
          opts,
        );
      });
    },
    format(code: string, opts: Omit<RunOptions, 'cache'> = {}): Promise<FmtResponse> {
      return exclusive(async () => {
        checkCode(code);
        const path = '/api/fmt';
        return request(
          path,
          JSON.stringify({ code }),
          cacheKeySource(path, code, []),
          validateFmtResponse,
          opts,
        );
      });
    },
  };
}
